import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Typography,
  useTheme,
  TextField,
  Chip
} from '@mui/material'

import GroupIcon from '@mui/icons-material/Group'
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark'
import { useSendInvitationMutation, useUpdateMemberPermissionsMutation } from 'src/services/white-label-team-management-service'
import ListAltIcon from '@mui/icons-material/ListAlt'
import { useGetAllPartnerUsersQuery } from 'src/services/partnerUser-service'
import { Formik, Form } from 'formik'
import toast from 'react-hot-toast'
import { ManageSubUsersStatus } from '../../config'
import * as Yup from 'yup'
import PageLoader from '../../utils/PageLoader'
import PrimaryButton from '../../components/Buttons/PrimaryButton'

const PermissionDialog = ({
  openManagePermissionsDialog,
  handleCloseManagePermissionsDialog,
  currentMember = null,
  user,
  fetchTeamMembers
}) => {
  const theme = useTheme()
  const { data: userList = [] } = useGetAllPartnerUsersQuery()
  const [manageSubUserDialogOpen, setManageSubUserDialogOpen] = useState(false)
  const [tempUserSelectionType, setTempUserSelectionType] = useState(ManageSubUsersStatus.NONE)
  const [confirmedUserSelectionType, setConfirmedUserSelectionType] = useState(ManageSubUsersStatus.NONE)
  const [tempSelectedUsers, setTempSelectedUsers] = useState([])
  const [confirmedSelectedUsers, setConfirmedSelectedUsers] = useState([])
  const [sendInvitation, { isLoading: adding }] = useSendInvitationMutation()
  const [updateMemberPermissions, { isLoading: updating }] = useUpdateMemberPermissionsMutation()

  const [formData, setFormData] = useState(
    {
      email: '',
      permissions: {
        manageSubUsers: ManageSubUsersStatus.NONE,
        manageBranding: false,
        managePlans: false,
        manageTeamMembers: false
      }
    }
  )

  useEffect(() => {
    if (currentMember) {
      const initPermissions = currentMember.permissions || {}
      setTempUserSelectionType(initPermissions.manageSubUsers || ManageSubUsersStatus.NONE)
      setConfirmedUserSelectionType(initPermissions.manageSubUsers || ManageSubUsersStatus.NONE)

      const assignedUsers = initPermissions.assignedSubUsers || []
      setTempSelectedUsers(assignedUsers)
      setConfirmedSelectedUsers(assignedUsers)

      setFormData(
        {
          email: currentMember.email,
          permissions: {
            manageSubUsers: initPermissions.manageSubUsers || ManageSubUsersStatus.NONE,
            manageBranding: initPermissions.manageBranding || false,
            managePlans: initPermissions.managePlans || false,
            manageTeamMembers: initPermissions.manageTeamMembers || false
          }
        }
      )
    } else {
      setTempUserSelectionType(ManageSubUsersStatus.NONE)
      setConfirmedUserSelectionType(ManageSubUsersStatus.NONE)
      setTempSelectedUsers([])
      setConfirmedSelectedUsers([])

      setFormData({
        email: '',
        permissions: {
          manageSubUsers: ManageSubUsersStatus.NONE,
          manageBranding: false,
          managePlans: false,
          manageTeamMembers: false
        }
      })
    }
  }, [currentMember])

  // Validation schema for Formik
  const validationSchema = Yup.object({
    email: Yup.string().email('Invalid email format').required('Email is required'),
    permissions: Yup.object()
      .shape({
        manageSubUsers: Yup.string()
          .oneOf(Object.values(ManageSubUsersStatus), 'Invalid manageSubUsers value')
          .default(ManageSubUsersStatus.NONE)
          .required('manageSubUsers is required'),
        manageBranding: Yup.boolean(),
        managePlans: Yup.boolean(),
        manageTeamMembers: Yup.boolean()
      })
      .test('at-least-one-selected', 'At least one permission must be enabled', (permissions) => {
        if (!permissions) return false

        const { manageSubUsers, ...otherPermissions } = permissions
        const hasSubUsersSelected = confirmedUserSelectionType && manageSubUsers !== ManageSubUsersStatus.NONE
        const hasOtherPermissionsSelected = Object.values(otherPermissions).some(value => value === true)
        return hasSubUsersSelected || hasOtherPermissionsSelected
      })
  })

  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError }) => {
    const { email } = values
    const trimmedEmail = email.trim()

    if (user?.email === trimmedEmail) {
      toast.error("You can't send an invite to the current user.")
      setSubmitting(false)
      return
    }

    try {
      const currentPermissions = {
        ...values.permissions,
        manageSubUsers: confirmedUserSelectionType,
        assignedSubUsers: confirmedUserSelectionType === 'Assigned' ? confirmedSelectedUsers : []
      }

      const requestBody = currentMember && currentMember._id
        ? {
            _id: currentMember?._id,
            userId:currentMember?.userId,
            permissions: currentPermissions
          }
        : {
            email: trimmedEmail,
            userId:currentMember?.userId,
            permissions: currentPermissions
          }

      const { data, error } = await (currentMember?._id ? updateMemberPermissions(requestBody) : sendInvitation(requestBody))
     
      if (error && error.data) {
        if (error.data?.error) {
          toast.error(error.data?.error?.message)
        } else {
          toast.error(error.data?.message)
        }
      } else if (data?.message === 'Already invited') {
        setFieldError('email', 'This member has already been invited.')
      } else {
        toast.success(data?.message)
        handleClose(resetForm, true)
        fetchTeamMembers && fetchTeamMembers()
      }
    } catch (error) {
      toast.error(error?.data ?? 'Failed to send the invitation. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const openManageTeamDialog = () => {
    setManageSubUserDialogOpen(true)
  }

  // Apply changes only when confirmed
  const handleConfirmSelection = () => {
    if (!tempUserSelectionType) {
      toast.error('Please select an access option.')
      return
    }
    if (tempUserSelectionType === 'Assigned' && tempSelectedUsers.length === 0) {
      toast.error('At least one user must be selected when "Assigned" is chosen.')
      return
    }

    setConfirmedUserSelectionType(tempUserSelectionType)
    tempUserSelectionType === ManageSubUsersStatus.ALL && setTempSelectedUsers([])
    setConfirmedSelectedUsers(tempSelectedUsers)
    setManageSubUserDialogOpen(false)
  }

  const handleCloseWithoutSaving = () => {
    setTempUserSelectionType(confirmedUserSelectionType)
    setTempSelectedUsers([...confirmedSelectedUsers])
    setManageSubUserDialogOpen(false)
  }

  // Reset all data when main dialog is closed
  const handleClose = (resetForm, closeWithSave = false) => {
    if (!currentMember) {
      setTempUserSelectionType(ManageSubUsersStatus.NONE)
      setConfirmedUserSelectionType(ManageSubUsersStatus.NONE)
      setTempSelectedUsers([])
      resetForm()
    }
    setManageSubUserDialogOpen(false)
    handleCloseManagePermissionsDialog(closeWithSave)
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{ ...formData }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values, handleChange, setFieldValue, touched, errors, resetForm }) => (
        <>
          <Dialog
            open={openManagePermissionsDialog}
            onClose={() => handleClose(resetForm)}
            PaperProps={{
              sx: { width: '100%', maxWidth: 400 }
            }}
            maxWidth='xs'
            fullWidth
          >
            <Form className='position-relative'>
              {
                   (updating || adding) && <PageLoader />
                }
              <DialogTitle>Manage Permissions </DialogTitle>
              <DialogContent>
                <TextField
                  id='email'
                  name='email'
                  label='Email'
                  variant='filled'
                  placeholder='Enter email'
                  fullWidth
                  value={values.email}
                  disabled={!!currentMember?.email}
                  onChange={handleChange}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  margin='normal'
                />

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <GroupIcon
                      sx={{ mr: 1.5, color: theme.palette.primary.main }}
                      fontSize='small'
                    />
                    <Typography variant='body2'>Manage Sub Users </Typography>
                  </Box>
                  <Switch
                    checked={confirmedUserSelectionType !== ManageSubUsersStatus.NONE}
                    onChange={(e) => {
                      if (e.target.checked) {
                        openManageTeamDialog()
                      } else {
                        setFieldValue('permissions.manageSubUsers', ManageSubUsersStatus.NONE)
                        setTempUserSelectionType(ManageSubUsersStatus.NONE)
                        setTempSelectedUsers([])
                        setConfirmedUserSelectionType(ManageSubUsersStatus.NONE)
                        setConfirmedSelectedUsers([])
                      }
                    }}
                  />
                </Box>

                {confirmedUserSelectionType !== ManageSubUsersStatus.NONE && (
                  <Box sx={{ pl: 4, mb: 2 }}>
                    <Typography variant='body2' sx={{ mb: 1 }}>
                      Current access: <Chip label={confirmedUserSelectionType} size='small' />
                    </Typography>
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={openManageTeamDialog}
                    >
                      Change Access Level
                    </Button>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant='subtitle2' sx={{ mb: 2 }}>
                  Other Permissions
                </Typography>

                <Box sx={{ mb: 2 }}>
                  {[
                    { label: 'Manage Branding / Domain', icon: BrandingWatermarkIcon, name: 'manageBranding' },
                    { label: 'Manage Plans', icon: ListAltIcon, name: 'managePlans' },
                    { label: 'Manage Team Members', icon: GroupIcon, name: 'manageTeamMembers' }
                  ].map(({ label, icon: Icon, name }) => (
                    <Box key={name} sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Icon sx={{ mr: 1.5, color: theme.palette.primary.main }} fontSize='small' />
                        <Typography variant='body2'>{label}</Typography>
                      </Box>
                      <Switch
                        checked={!!values.permissions[name]}
                        onChange={(e) => setFieldValue(`permissions.${name}`, e.target.checked)}
                      />
                    </Box>
                  ))}
                </Box>
                {touched.permissions && errors.permissions && (
                  <Typography variant='body2' color='error' sx={{ pl: 2, fontSize: '0.75rem' }}>
                    {errors.permissions}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => handleClose(resetForm)} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type='submit' color='primary' variant='contained' disabled={isSubmitting}>
                  Save
                </Button>
              </DialogActions>
            </Form>
          </Dialog>

          {/* Sub User access selection dialog */}
          <Dialog
            open={manageSubUserDialogOpen}
            onClose={() => handleCloseWithoutSaving}
            maxWidth='sm'
            fullWidth
          >
            <DialogTitle>Select User Access Level</DialogTitle>
            <DialogContent>
              <Typography
                variant='body2'
                sx={{ mb: 2, mt: 1, color: theme.palette.secondary }}
              >
                Choose how {currentMember?.name?.first} {currentMember?.name?.last} can manage sub users
              </Typography>

              <FormControl fullWidth margin='normal'>
                <InputLabel id='user-access-label'>User Access</InputLabel>
                <Select
                  labelId='user-access-label'
                  value={tempUserSelectionType}
                  onChange={(e) => setTempUserSelectionType(e.target.value)}
                  label='User Access'
                >
                  <MenuItem value='All'>All Sub Users</MenuItem>
                  <MenuItem value='Assigned'>Only Assigned Sub Users</MenuItem>
                  <MenuItem value='None' disabled>
                    No Access
                  </MenuItem>
                </Select>
              </FormControl>

              {tempUserSelectionType === 'Assigned' && userList.users && userList.users.docs && (
                <>
                  <Typography variant='subtitle2' sx={{ mt: 3, mb: 1 }}>
                    Select Specific Users:
                  </Typography>
                  <List
                    sx={{
                      maxHeight: 300,
                      overflowY: 'auto',
                      border: '1px solid #eee',
                      borderRadius: 1
                    }}
                  >
                    {userList.users.docs.map((user) => (
                      <ListItem
                        key={user._id}
                        onClick={() => {
                          setTempSelectedUsers((prev) =>
                            prev.includes(user._id)
                              ? prev.filter((id) => id !== user._id)
                              : [...prev, user._id]
                          )
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox checked={(tempSelectedUsers.includes(user._id))} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${user.name.first} ${user.name.last}`}
                          secondary={user.email}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <PrimaryButton
                buttonText='Cancel'
                onClick={handleCloseWithoutSaving}
                variant='outlined'
              />
              <PrimaryButton
                buttonText='Confirm'
                onClick={() => {
                  setFieldValue('permissions.manageSubUsers', tempUserSelectionType)
                  handleConfirmSelection()
                }}
                variant='contained'
                disabled={!tempUserSelectionType}
              />
            </DialogActions>
          </Dialog>
        </>
      )}
    </Formik>
  )
}

export default PermissionDialog
